/**
 * OutcomeGroupContainer — applies the group's display cap.
 *
 * The View used to slice the entry list and subtract to find how many were
 * hidden. Both are decisions about WHAT to show, which is Container work
 * (SE-8019); the View draws the list it is handed and says how many are left.
 * @module features/player-kanban/components/organisms/ClosedArea/OutcomeGroup/OutcomeGroupContainer
 */

import React, { memo } from "react";

import OutcomeGroupView from "./OutcomeGroupView";

import type { ClosedCardEntry, ClosedGroupData } from "../../../../types/types";

/**
 * How many entries a group shows before the cap is lifted.
 *
 * One: the Closed area is a summary rail, not a list. It lives here rather than
 * in the View because applying the cap is the Container's job now (SE-8019).
 */
const VISIBLE_ENTRIES_CAP = 1;

/** Props for {@link OutcomeGroupContainer}. */
export interface OutcomeGroupContainerProps {
  /** The group to render, with its entries already ordered. */
  readonly group: ClosedGroupData;
  /** Whether this group's one-card cap has been lifted. */
  readonly isShowingAll: boolean;
  /** Whether the viewer may reopen cards. */
  readonly canReopen: boolean;
  /** Reopen-request handler for a specific closed card. */
  readonly onReopenPress: (entry: ClosedCardEntry) => void;
  /** Lift the cap for this group. */
  readonly onShowMore?: () => void;
}

/**
 * Applies the cap and renders the group.
 * @param props - {@link OutcomeGroupContainerProps}
 * @param props.group - The group to render
 * @param props.isShowingAll - Whether the cap has been lifted
 * @param props.canReopen - Whether the viewer may reopen cards
 * @param props.onReopenPress - Reopen-request handler
 * @param props.onShowMore - Lift the cap for this group
 * @returns The rendered group.
 */
function OutcomeGroupContainer({
  group,
  isShowingAll,
  canReopen,
  onReopenPress,
  onShowMore,
}: OutcomeGroupContainerProps) {
  const visibleEntries = isShowingAll
    ? group.entries
    : group.entries.slice(0, VISIBLE_ENTRIES_CAP);

  return (
    <OutcomeGroupView
      group={group}
      visibleEntries={visibleEntries}
      // `group.count` is the group's TOTAL, which can exceed the entries the
      // page actually loaded — so the hidden count is the difference against
      // that total, not against `entries.length`.
      hiddenCount={group.count - visibleEntries.length}
      canReopen={canReopen}
      onReopenPress={onReopenPress}
      onShowMore={onShowMore}
    />
  );
}

OutcomeGroupContainer.displayName = "OutcomeGroupContainer";

export default memo(OutcomeGroupContainer);
